package com.patronat.top.client.presentation.home.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.patronat.top.client.location.ClientLocationSuggestion
import com.patronat.top.ui.atoms.AppSearchField
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.BodyMedium
import com.patronat.top.ui.theme.MineShaft100
import com.patronat.top.ui.theme.MineShaft500
import com.patronat.top.ui.theme.MineShaft900
import com.patronat.top.ui.theme.MineShaft950
import com.patronat.top.ui.theme.Primary600
import com.patronat.top.ui.theme.White
import org.jetbrains.compose.resources.stringResource
import top.clientapp.generated.resources.Res
import top.clientapp.generated.resources.client_location_search_back_content_description
import top.clientapp.generated.resources.client_location_search_empty
import top.clientapp.generated.resources.client_location_search_placeholder
import top.clientapp.generated.resources.client_location_search_title

@Composable
fun LocationSearchContent(
    query: String,
    suggestions: List<ClientLocationSuggestion>,
    isLoading: Boolean,
    onBackClick: () -> Unit,
    onQueryChange: (String) -> Unit,
    onSuggestionClick: (ClientLocationSuggestion) -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxSize()
            .background(White)
            .padding(horizontal = 24.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(48.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .size(36.dp)
                    .clickable(onClick = onBackClick),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    painter = AppIcons.ArrowLeft,
                    contentDescription = stringResource(Res.string.client_location_search_back_content_description),
                    tint = MineShaft950,
                    modifier = Modifier.size(20.dp)
                )
            }

            Text(
                text = stringResource(Res.string.client_location_search_title),
                style = BodyMedium.copy(
                    color = MineShaft950,
                    fontSize = 14.sp,
                    lineHeight = 20.sp,
                    fontWeight = FontWeight.Medium
                )
            )
        }

        Spacer(modifier = Modifier.height(16.dp))

        AppSearchField(
            value = query,
            onValueChange = onQueryChange,
            placeholder = stringResource(Res.string.client_location_search_placeholder),
            modifier = Modifier.fillMaxWidth()
        )

        Spacer(modifier = Modifier.height(16.dp))

        when {
            isLoading -> {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 28.dp),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator(color = Primary600)
                }
            }

            query.length > 2 && suggestions.isEmpty() -> {
                Text(
                    text = stringResource(Res.string.client_location_search_empty),
                    style = BodyMedium.copy(color = MineShaft500),
                    modifier = Modifier.padding(top = 16.dp)
                )
            }

            else -> {
                LazyColumn(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(0.dp)
                ) {
                    items(
                        items = suggestions,
                        key = { it.id }
                    ) { suggestion ->
                        LocationSuggestionRow(
                            suggestion = suggestion,
                            onClick = { onSuggestionClick(suggestion) }
                        )
                        HorizontalDivider(color = MineShaft100)
                    }
                }
            }
        }
    }
}

@Composable
private fun LocationSuggestionRow(
    suggestion: ClientLocationSuggestion,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(vertical = 14.dp),
        horizontalArrangement = Arrangement.spacedBy(12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(
            painter = AppIcons.VenueLocation,
            contentDescription = null,
            tint = Primary600,
            modifier = Modifier.size(20.dp)
        )

        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = suggestion.title,
                style = BodyMedium.copy(
                    color = MineShaft900,
                    fontSize = 15.sp,
                    lineHeight = 22.sp,
                    fontWeight = FontWeight.Medium
                ),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )

            Text(
                text = suggestion.placeName,
                style = BodyMedium.copy(
                    color = MineShaft500,
                    fontSize = 13.sp,
                    lineHeight = 19.sp
                ),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}
